import { DestroyRef, inject, Injectable, Service, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { of, Subject, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap} from 'rxjs/operators';

import { OrderTypeEnum } from '../enums/order-type.enum.model';
import { UtilityFunctions } from '../utilities/functions.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type NavigationMode = 'assortment' | 'promotion';

/**
 * Store di dominio per la modalità di navigazione dello shop (ADR 002):
 * shop mode (pre-order / re-order, guidato dalle route), navigation mode
 * (assortment / promotion) e modalità solo-retail.
 * Fonte di verità: i signal; ogni scrittura passa dai setter.
 */
@Service()
export class NavigationStore {
  private readonly _router = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);

  private readonly _currentShopMode = signal<OrderTypeEnum | null>(null);
  readonly currentShopMode = this._currentShopMode.asReadonly();

  private readonly _navigationMode = signal<NavigationMode>('assortment');
  readonly navigationMode = this._navigationMode.asReadonly();

  private readonly _onlyRetailMode = signal<boolean | null>(null);
  readonly onlyRetailMode = this._onlyRetailMode.asReadonly();

  private destroyRef = inject(DestroyRef);

  constructor() {
    this.listenRouteShopMode();
  }

  setShopMode(mode: OrderTypeEnum): void {
    this._currentShopMode.set(mode);
    localStorage.setItem('navigationMode', mode === OrderTypeEnum.ReOrder ? 're-order' : 'pre-order');
  }

  setNavigationMode(mode: NavigationMode): void {
    this._navigationMode.set(mode);
  }

  setOnlyRetailMode(value: boolean, options?: { skipLocalStorageUpdate?: boolean }): void {
    this._onlyRetailMode.set(value);
    if (!options?.skipLocalStorageUpdate) {
      UtilityFunctions.updateLocalStorageIfChanged('onlyRetailMode', value);
    }
  }

  /**
   * Polling di localStorage 'onlyRetailMode' ogni 500ms — debito storico
   * preservato tale e quale (vedi plan migrazione, rischio #4): il valore è
   * scritto da flussi esterni e riletto qui. destroy$ non viene mai emesso,
   * come nell'implementazione originale di CoreService.checkNavigationMode.
   */
  startRetailModeLocalStorageSync(): void {
    timer(0, 500).pipe(
      switchMap(() => of(localStorage.getItem('onlyRetailMode'))),
      distinctUntilChanged(),
      filter(value => !!value),
      map(value => value === 'true'),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(value => this.setOnlyRetailMode(value));
  }

  private listenRouteShopMode(): void {
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this._activatedRoute),
      map(route => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter(route => route.outlet === 'primary'),
      switchMap(route => route.data)
    ).subscribe(event => {
      if (event['route'] === OrderTypeEnum.PreOrder && this._currentShopMode() !== OrderTypeEnum.PreOrder) this.setShopMode(OrderTypeEnum.PreOrder);
      if (event['route'] === OrderTypeEnum.ReOrder && this._currentShopMode() !== OrderTypeEnum.ReOrder) this.setShopMode(OrderTypeEnum.ReOrder);
    });
  }
}
